// src/rentals-api-v3/models/Address.ts
function AddressFromJSON(json) {
  return AddressFromJSONTyped(json, false);
}
function AddressFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    street: json["street"] == null ? void 0 : json["street"],
    state: json["state"] == null ? void 0 : json["state"],
    city: json["city"] == null ? void 0 : json["city"],
    country: json["country"] == null ? void 0 : json["country"],
    postCode: json["postCode"] == null ? void 0 : json["postCode"]
  };
}

// src/rentals-api-v3/models/CurrencyValue.ts
function CurrencyValueFromJSON(json) {
  return CurrencyValueFromJSONTyped(json, false);
}
function CurrencyValueFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    currencyCode: json["currencyCode"],
    value: json["value"]
  };
}

// src/rentals-api-v3/models/FleetTypeCode.ts
function FleetTypeCodeFromJSON(json) {
  return FleetTypeCodeFromJSONTyped(json, false);
}
function FleetTypeCodeFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/runtime.ts
import { formatISO } from "date-fns";
var BASE_PATH = "https://rentals.api.jucy.cloud".replace(/\/+$/, "");
var Configuration = class {
  constructor(configuration = {}) {
    this.configuration = configuration;
  }
  set config(configuration) {
    this.configuration = configuration;
  }
  get basePath() {
    return this.configuration.basePath != null ? this.configuration.basePath : BASE_PATH;
  }
  get fetchApi() {
    return this.configuration.fetchApi || fetch;
  }
  get middleware() {
    return this.configuration.middleware || [];
  }
  get queryParamsStringify() {
    return this.configuration.queryParamsStringify || querystring;
  }
  get username() {
    return this.configuration.username;
  }
  get password() {
    return this.configuration.password;
  }
  get apiKey() {
    const apiKey = this.configuration.apiKey;
    if (apiKey) {
      return typeof apiKey === "function" ? apiKey : () => apiKey;
    }
    return void 0;
  }
  get accessToken() {
    const accessToken = this.configuration.accessToken;
    if (accessToken) {
      return typeof accessToken === "function" ? accessToken : async () => accessToken;
    }
    return void 0;
  }
  get headers() {
    return this.configuration.headers;
  }
  get credentials() {
    return this.configuration.credentials;
  }
};
var DefaultConfig = new Configuration();
async function createResponseErrorMessage(response, fallback = "Response returned an error code") {
  try {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.startsWith("application/json")) {
      const json = await response.json();
      return new ResponseError(response, json?.message || json?.Message || response.statusText || fallback, json);
    }
    const body = await response.text();
    return new ResponseError(response, body || response.statusText || fallback, { body });
  } catch (e) {
    return new ResponseError(response, fallback, { error: e });
  }
}
var BaseAPI = class _BaseAPI {
  constructor(configuration = DefaultConfig) {
    this.configuration = configuration;
    this.middleware = configuration.middleware;
  }
  static jsonRegex = new RegExp("^(:?application/json|[^;/ 	]+/[^;/ 	]+[+]json)[ 	]*(:?;.*)?$", "i");
  middleware;
  withMiddleware(...middlewares) {
    const next = this.clone();
    next.middleware = next.middleware.concat(...middlewares);
    return next;
  }
  withPreMiddleware(...preMiddlewares) {
    const middlewares = preMiddlewares.map((pre) => ({ pre }));
    return this.withMiddleware(...middlewares);
  }
  withPostMiddleware(...postMiddlewares) {
    const middlewares = postMiddlewares.map((post) => ({ post }));
    return this.withMiddleware(...middlewares);
  }
  /**
   * Check if the given MIME is a JSON MIME.
   * JSON MIME examples:
   *   application/json
   *   application/json; charset=UTF8
   *   APPLICATION/JSON
   *   application/vnd.company+json
   * @param mime - MIME (Multipurpose Internet Mail Extensions)
   * @return True if the given MIME is JSON, false otherwise.
   */
  isJsonMime(mime) {
    if (!mime) {
      return false;
    }
    return _BaseAPI.jsonRegex.test(mime);
  }
  async request(context, initOverrides) {
    const { url, init } = await this.createFetchParams(context, initOverrides);
    const response = await this.fetchApi(url, init);
    if (response && response.status >= 200 && response.status < 300) {
      return response;
    }
    throw await createResponseErrorMessage(response, "Response returned an error code");
  }
  async createFetchParams(context, initOverrides) {
    let url = this.configuration.basePath + context.path;
    if (context.query !== void 0 && Object.keys(context.query).length !== 0) {
      url += "?" + this.configuration.queryParamsStringify(context.query);
    }
    const headers = Object.assign({}, this.configuration.headers, context.headers);
    Object.keys(headers).forEach((key) => headers[key] === void 0 ? delete headers[key] : {});
    const initOverrideFn = typeof initOverrides === "function" ? initOverrides : async () => initOverrides;
    const initParams = {
      method: context.method,
      headers,
      body: context.body,
      credentials: this.configuration.credentials
    };
    const overriddenInit = {
      ...initParams,
      ...await initOverrideFn({
        init: initParams,
        context
      })
    };
    let body;
    if (isFormData(overriddenInit.body) || overriddenInit.body instanceof URLSearchParams || isBlob(overriddenInit.body)) {
      body = overriddenInit.body;
    } else if (this.isJsonMime(headers["Content-Type"])) {
      body = JSON.stringify(overriddenInit.body);
    } else {
      body = overriddenInit.body;
    }
    const init = {
      ...overriddenInit,
      body
    };
    return { url, init };
  }
  fetchApi = async (url, init) => {
    let fetchParams = { url, init };
    for (const middleware of this.middleware) {
      if (middleware.pre) {
        fetchParams = await middleware.pre({
          fetch: this.fetchApi,
          ...fetchParams
        }) || fetchParams;
      }
    }
    let response = void 0;
    try {
      response = await (this.configuration.fetchApi || fetch)(fetchParams.url, fetchParams.init);
    } catch (e) {
      for (const middleware of this.middleware) {
        if (middleware.onError) {
          response = await middleware.onError({
            fetch: this.fetchApi,
            url: fetchParams.url,
            init: fetchParams.init,
            error: e,
            response: response ? response.clone() : void 0
          }) || response;
        }
      }
      if (response === void 0) {
        if (e instanceof Error) {
          throw new FetchError(e, "The request failed and the interceptors did not return an alternative response");
        } else {
          throw e;
        }
      }
    }
    for (const middleware of this.middleware) {
      if (middleware.post) {
        response = await middleware.post({
          fetch: this.fetchApi,
          url: fetchParams.url,
          init: fetchParams.init,
          response: response.clone()
        }) || response;
      }
    }
    return response;
  };
  /**
   * Create a shallow clone of `this` by constructing a new instance
   * and then shallow cloning data members.
   */
  clone() {
    const constructor = this.constructor;
    const next = new constructor(this.configuration);
    next.middleware = this.middleware.slice();
    return next;
  }
};
function isBlob(value) {
  return typeof Blob !== "undefined" && value instanceof Blob;
}
function isFormData(value) {
  return typeof FormData !== "undefined" && value instanceof FormData;
}
function stringToDate(value) {
  return value ? new Date(value.replace(/Z$/, "")) : /* @__PURE__ */ new Date("invalid");
}
function dateToString(value) {
  return value ? formatISO(value)?.replace(/Z$/, "") : void 0;
}
var ResponseError = class extends Error {
  constructor(response, msg, data) {
    super(msg);
    this.response = response;
    this.data = data;
  }
  name = "ResponseError";
  data;
};
var FetchError = class extends Error {
  constructor(cause, msg) {
    super(msg);
    this.cause = cause;
  }
  name = "FetchError";
};
var RequiredError = class extends Error {
  constructor(field, msg) {
    super(msg);
    this.field = field;
  }
  name = "RequiredError";
};
function querystring(params, prefix = "") {
  return Object.keys(params).map((key) => querystringSingleKey(key, params[key], prefix)).filter((part) => part.length > 0).join("&");
}
function querystringSingleKey(key, value, keyPrefix = "") {
  const fullKey = keyPrefix + (keyPrefix.length ? `[${key}]` : key);
  if (value instanceof Array) {
    const multiValue = value.map((singleValue) => encodeURIComponent(String(singleValue))).join(`&${encodeURIComponent(fullKey)}=`);
    return `${encodeURIComponent(fullKey)}=${multiValue}`;
  }
  if (value instanceof Set) {
    const valueAsArray = Array.from(value);
    return querystringSingleKey(key, valueAsArray, keyPrefix);
  }
  if (value instanceof Date) {
    return `${encodeURIComponent(fullKey)}=${encodeURIComponent(value.toISOString())}`;
  }
  if (value instanceof Object) {
    return querystring(value, fullKey);
  }
  return `${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`;
}
var JSONApiResponse = class {
  constructor(raw, transformer = (jsonValue) => jsonValue) {
    this.raw = raw;
    this.transformer = transformer;
  }
  async value() {
    return this.transformer(await this.raw.json());
  }
};

// src/rentals-api-v3/models/ProductCategory.ts
function ProductCategoryFromJSON(json) {
  return ProductCategoryFromJSONTyped(json, false);
}
function ProductCategoryFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/MandatoryProduct.ts
function MandatoryProductFromJSON(json) {
  return MandatoryProductFromJSONTyped(json, false);
}
function MandatoryProductFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: ProductCategoryFromJSON(json["category"]),
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"]),
    qty: json["qty"],
    total: CurrencyValueFromJSON(json["total"]),
    isExcluded: json["isExcluded"] == null ? void 0 : json["isExcluded"]
  };
}

// src/rentals-api-v3/models/RewardPoints1.ts
function RewardPoints1FromJSON(json) {
  return RewardPoints1FromJSONTyped(json, false);
}
function RewardPoints1FromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    identifier: json["identifier"],
    bonusPoints: json["bonusPoints"],
    pointMultiplier: json["pointMultiplier"],
    type: json["type"]
  };
}

// src/rentals-api-v3/models/FleetCategory.ts
function FleetCategoryFromJSON(json) {
  return FleetCategoryFromJSONTyped(json, false);
}
function FleetCategoryFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    name: json["name"],
    description: json["description"],
    code: json["code"],
    categoryCode: json["categoryCode"],
    campaignCode: json["campaignCode"] == null ? void 0 : json["campaignCode"],
    campaignId: json["campaignId"] == null ? void 0 : json["campaignId"],
    id: json["id"],
    type: json["type"],
    rentalDays: json["rentalDays"],
    dealText: json["dealText"] == null ? void 0 : json["dealText"],
    freeDays: json["freeDays"] == null ? void 0 : json["freeDays"],
    fleetTypeCode: json["fleetTypeCode"],
    availabilityMessage: json["availabilityMessage"],
    dailyRate: CurrencyValueFromJSON(json["dailyRate"]),
    total: CurrencyValueFromJSON(json["total"]),
    discountPercentageApplied: json["discountPercentageApplied"] == null ? void 0 : json["discountPercentageApplied"],
    mandatoryFees: json["mandatoryFees"] == null ? void 0 : json["mandatoryFees"].map(MandatoryProductFromJSON),
    minimumHirePeriod: json["minimumHirePeriod"] == null ? void 0 : json["minimumHirePeriod"],
    isAfterHourPickUp: json["isAfterHourPickUp"] == null ? void 0 : json["isAfterHourPickUp"],
    rewardPoints: json["rewardPoints"] == null ? void 0 : RewardPoints1FromJSON(json["rewardPoints"]),
    availability: json["availability"]
  };
}

// src/rentals-api-v3/models/AvailabilityType.ts
function AvailabilityTypeFromJSON(json) {
  return AvailabilityTypeFromJSONTyped(json, false);
}
function AvailabilityTypeFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/InsuranceType.ts
function InsuranceTypeFromJSON(json) {
  return InsuranceTypeFromJSONTyped(json, false);
}
function InsuranceTypeFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/BookingLineItem.ts
function BookingLineItemFromJSON(json) {
  return BookingLineItemFromJSONTyped(json, false);
}
function BookingLineItemFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: ProductCategoryFromJSON(json["category"]),
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"]),
    qty: json["qty"],
    total: CurrencyValueFromJSON(json["total"]),
    isExcluded: json["isExcluded"] == null ? void 0 : json["isExcluded"],
    excessType: InsuranceTypeFromJSON(json["excessType"]),
    excess: CurrencyValueFromJSON(json["excess"]),
    bond: CurrencyValueFromJSON(json["bond"])
  };
}

// src/rentals-api-v3/models/BookingPaymentInfo.ts
function BookingPaymentInfoFromJSON(json) {
  return BookingPaymentInfoFromJSONTyped(json, false);
}
function BookingPaymentInfoFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    amount: CurrencyValueFromJSON(json["amount"]),
    date: stringToDate(json["date"]),
    type: json["type"],
    merchantFeeName: json["merchantFeeName"] == null ? void 0 : json["merchantFeeName"],
    merchantFeeAmount: json["merchantFeeAmount"] == null ? void 0 : CurrencyValueFromJSON(json["merchantFeeAmount"]),
    total: json["total"] == null ? void 0 : CurrencyValueFromJSON(json["total"])
  };
}

// src/rentals-api-v3/models/BookingStatus.ts
function BookingStatusFromJSON(json) {
  return BookingStatusFromJSONTyped(json, false);
}
function BookingStatusFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/DriversLicence.ts
function DriversLicenceFromJSON(json) {
  return DriversLicenceFromJSONTyped(json, false);
}
function DriversLicenceFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    expiry: json["expiry"] == null ? void 0 : stringToDate(json["expiry"]),
    country: json["country"],
    number: json["number"] == null ? void 0 : json["number"]
  };
}

// src/rentals-api-v3/models/HirerDetails.ts
function HirerDetailsFromJSON(json) {
  return HirerDetailsFromJSONTyped(json, false);
}
function HirerDetailsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    firstName: json["firstName"],
    lastName: json["lastName"],
    email: json["email"],
    mobileNumber: json["mobileNumber"],
    address: json["address"] == null ? void 0 : AddressFromJSON(json["address"]),
    dateOfBirth: json["dateOfBirth"] == null ? void 0 : stringToDate(json["dateOfBirth"]),
    driversLicence: json["driversLicence"] == null ? void 0 : DriversLicenceFromJSON(json["driversLicence"]),
    lucyClubNumber: json["lucyClubNumber"] == null ? void 0 : json["lucyClubNumber"],
    mailingList: json["mailingList"] == null ? void 0 : json["mailingList"],
    arrivalFlightNumber: json["arrivalFlightNumber"] == null ? void 0 : json["arrivalFlightNumber"],
    departureFlightNumber: json["departureFlightNumber"] == null ? void 0 : json["departureFlightNumber"],
    numberOfPeople: json["numberOfPeople"] == null ? void 0 : json["numberOfPeople"],
    loyalty: json["loyalty"] == null ? void 0 : json["loyalty"]
  };
}

// src/rentals-api-v3/models/Booking.ts
function BookingFromJSON(json) {
  return BookingFromJSONTyped(json, false);
}
function BookingFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    quoteId: json["quoteId"],
    reservationId: json["reservationId"],
    reservationReference: json["reservationReference"],
    agentBalanceDue: json["agentBalanceDue"] == null ? void 0 : CurrencyValueFromJSON(json["agentBalanceDue"]),
    agentCollects: json["agentCollects"] == null ? void 0 : CurrencyValueFromJSON(json["agentCollects"]),
    agentCommission: json["agentCommission"] == null ? void 0 : CurrencyValueFromJSON(json["agentCommission"]),
    voucherReference: json["voucherReference"] == null ? void 0 : json["voucherReference"],
    agentName: json["agentName"] == null ? void 0 : json["agentName"],
    balanceDue: json["balanceDue"] == null ? void 0 : CurrencyValueFromJSON(json["balanceDue"]),
    payments: json["payments"] == null ? void 0 : json["payments"].map(BookingPaymentInfoFromJSON),
    status: BookingStatusFromJSON(json["status"]),
    pickUpLocation: json["pickUpLocation"],
    dropOffLocation: json["dropOffLocation"],
    dateCreated: stringToDate(json["dateCreated"]),
    pickUpDate: stringToDate(json["pickUpDate"]),
    dropOffDate: stringToDate(json["dropOffDate"]),
    rentalDays: json["rentalDays"],
    totalPrice: CurrencyValueFromJSON(json["totalPrice"]),
    driverAge: json["driverAge"],
    lines: json["lines"] == null ? void 0 : json["lines"].map(BookingLineItemFromJSON),
    hirerDetails: json["hirerDetails"] == null ? void 0 : HirerDetailsFromJSON(json["hirerDetails"]),
    fleetCategory: FleetCategoryFromJSON(json["fleetCategory"]),
    referrerCode: json["referrerCode"] == null ? void 0 : json["referrerCode"]
  };
}

// src/rentals-api-v3/models/InsuranceProduct.ts
function InsuranceProductFromJSON(json) {
  return InsuranceProductFromJSONTyped(json, false);
}
function InsuranceProductFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: ProductCategoryFromJSON(json["category"]),
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"]),
    excessType: InsuranceTypeFromJSON(json["excessType"]),
    excess: CurrencyValueFromJSON(json["excess"]),
    bond: CurrencyValueFromJSON(json["bond"]),
    rcmId: json["rcmId"] == null ? void 0 : json["rcmId"]
  };
}

// src/rentals-api-v3/models/InsuranceProductGroup.ts
function InsuranceProductGroupFromJSON(json) {
  return InsuranceProductGroupFromJSONTyped(json, false);
}
function InsuranceProductGroupFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    group: json["group"],
    webCategory: json["webCategory"],
    required: json["required"],
    allowMultiple: json["allowMultiple"],
    description: json["description"],
    items: json["items"].map(InsuranceProductFromJSON)
  };
}

// src/rentals-api-v3/models/SecondaryProduct.ts
function SecondaryProductFromJSON(json) {
  return SecondaryProductFromJSONTyped(json, false);
}
function SecondaryProductFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: json["category"],
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"])
  };
}

// src/rentals-api-v3/models/ProductBundle.ts
function ProductBundleFromJSON(json) {
  return ProductBundleFromJSONTyped(json, false);
}
function ProductBundleFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    productCode: json["productCode"],
    bundledProducts: json["bundledProducts"].map(SecondaryProductFromJSON)
  };
}

// src/rentals-api-v3/models/SecondaryProductGroup.ts
function SecondaryProductGroupFromJSON(json) {
  return SecondaryProductGroupFromJSONTyped(json, false);
}
function SecondaryProductGroupFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    group: json["group"],
    webCategory: json["webCategory"],
    required: json["required"],
    allowMultiple: json["allowMultiple"],
    description: json["description"],
    items: json["items"].map(SecondaryProductFromJSON)
  };
}

// src/rentals-api-v3/models/BookingOptions.ts
function BookingOptionsFromJSON(json) {
  return BookingOptionsFromJSONTyped(json, false);
}
function BookingOptionsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    secondaryProducts: json["secondaryProducts"].map(SecondaryProductGroupFromJSON),
    insuranceProducts: InsuranceProductGroupFromJSON(json["insuranceProducts"]),
    bundles: json["bundles"] == null ? void 0 : json["bundles"].map(ProductBundleFromJSON)
  };
}

// src/rentals-api-v3/models/BookingSummaryStatus.ts
function BookingSummaryStatusFromJSON(json) {
  return BookingSummaryStatusFromJSONTyped(json, false);
}
function BookingSummaryStatusFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/BookingType.ts
function BookingTypeToJSON(value) {
  return value;
}

// src/rentals-api-v3/models/CancelReservationResponse.ts
function CancelReservationResponseFromJSON(json) {
  return CancelReservationResponseFromJSONTyped(json, false);
}
function CancelReservationResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    message: json["message"],
    reservationRef: json["reservationRef"]
  };
}

// src/rentals-api-v3/models/CreateBookingResponseStatus.ts
function CreateBookingResponseStatusFromJSON(json) {
  return CreateBookingResponseStatusFromJSONTyped(json, false);
}
function CreateBookingResponseStatusFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/HirerDetailsSummary.ts
function HirerDetailsSummaryToJSON(json) {
  return HirerDetailsSummaryToJSONTyped(json, false);
}
function HirerDetailsSummaryToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    firstName: value["firstName"],
    lastName: value["lastName"],
    mobileNumber: value["mobileNumber"],
    email: value["email"],
    driversLicenceCountry: value["driversLicenceCountry"],
    acceptedTerms: value["acceptedTerms"],
    mailingList: value["mailingList"],
    loyalty: value["loyalty"],
    dateOfBirth: value["dateOfBirth"] == null ? void 0 : dateToString(value["dateOfBirth"])?.substring(0, 10)
  };
}

// src/rentals-api-v3/models/CreateBookingRequest.ts
function CreateBookingRequestToJSON(json) {
  return CreateBookingRequestToJSONTyped(json, false);
}
function CreateBookingRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    pickUpLocation: value["pickUpLocation"],
    dropOffLocation: value["dropOffLocation"],
    pickUpDate: value["pickUpDate"],
    dropOffDate: value["dropOffDate"],
    fleetCategory: value["fleetCategory"],
    driverAge: value["driverAge"],
    numberOfPeople: value["numberOfPeople"],
    campaignCode: value["campaignCode"],
    pascal: value["pascal"],
    type: BookingTypeToJSON(value["type"]),
    insurance: value["insurance"],
    secondaryProducts: value["secondaryProducts"],
    hirerDetails: HirerDetailsSummaryToJSON(value["hirerDetails"]),
    voucherReference: value["voucherReference"],
    agentName: value["agentName"],
    gateway: value["gateway"],
    payment: value["payment"]
  };
}

// src/rentals-api-v3/models/CreateBookingResponse.ts
function CreateBookingResponseFromJSON(json) {
  return CreateBookingResponseFromJSONTyped(json, false);
}
function CreateBookingResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    booking: BookingFromJSON(json["booking"]),
    status: CreateBookingResponseStatusFromJSON(json["status"]),
    convertUrl: json["convertUrl"] == null ? void 0 : json["convertUrl"],
    reference: json["reference"],
    bookingNo: json["bookingNo"],
    paymentToken: json["paymentToken"] == null ? void 0 : json["paymentToken"]
  };
}

// src/rentals-api-v3/models/FindReservationRequest.ts
function FindReservationRequestToJSON(json) {
  return FindReservationRequestToJSONTyped(json, false);
}
function FindReservationRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    lastName: value["lastName"],
    bookingNumber: value["bookingNumber"],
    pickUpBranchCode: value["pickUpBranchCode"]
  };
}

// src/rentals-api-v3/models/FindReservationResultHirer.ts
function FindReservationResultHirerFromJSON(json) {
  return FindReservationResultHirerFromJSONTyped(json, false);
}
function FindReservationResultHirerFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    firstName: json["firstName"],
    lastName: json["lastName"],
    email: json["email"],
    checkedIn: json["checkedIn"] == null ? void 0 : json["checkedIn"]
  };
}

// src/rentals-api-v3/models/FindReservationResult.ts
function FindReservationResultFromJSON(json) {
  return FindReservationResultFromJSONTyped(json, false);
}
function FindReservationResultFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    id: json["id"],
    reservationId: json["reservationId"] == null ? void 0 : json["reservationId"],
    allocatedReservationId: json["allocatedReservationId"] == null ? void 0 : json["allocatedReservationId"],
    accountId: json["accountId"] == null ? void 0 : json["accountId"],
    ref: json["ref"],
    status: BookingSummaryStatusFromJSON(json["status"]),
    voucherRef: json["voucherRef"] == null ? void 0 : json["voucherRef"],
    allocated: json["allocated"] == null ? void 0 : json["allocated"],
    rego: json["rego"] == null ? void 0 : json["rego"],
    availability: AvailabilityTypeFromJSON(json["availability"]),
    created: stringToDate(json["created"]),
    updated: stringToDate(json["updated"]),
    hirer: json["hirer"] == null ? void 0 : FindReservationResultHirerFromJSON(json["hirer"]),
    fleetTypeCode: FleetTypeCodeFromJSON(json["fleetTypeCode"]),
    pickUpSiteCode: json["pickUpSiteCode"],
    dropOffSiteCode: json["dropOffSiteCode"],
    fleetCategoryCode: json["fleetCategoryCode"],
    rentalDays: json["rentalDays"],
    pickUpDate: stringToDate(json["pickUpDate"]),
    dropOffDate: stringToDate(json["dropOffDate"])
  };
}

// src/rentals-api-v3/models/ProductLine.ts
function ProductLineFromJSON(json) {
  return ProductLineFromJSONTyped(json, false);
}
function ProductLineFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    ...json,
    productId: json["productId"],
    sortOrder: json["sortOrder"],
    productCode: json["productCode"],
    name: json["name"],
    category: ProductCategoryFromJSON(json["category"]),
    isMandatory: json["isMandatory"],
    description: json["description"],
    price: CurrencyValueFromJSON(json["price"]),
    productType: json["productType"],
    isPercentageOnTotalCost: json["isPercentageOnTotalCost"],
    allowMultiple: json["allowMultiple"],
    commissionable: json["commissionable"],
    associatedProductCode: json["associatedProductCode"] == null ? void 0 : json["associatedProductCode"],
    rentalDays: json["rentalDays"],
    numberOfDaysCalculated: json["numberOfDaysCalculated"],
    maxPrice: json["maxPrice"] == null ? void 0 : CurrencyValueFromJSON(json["maxPrice"]),
    qty: json["qty"],
    total: CurrencyValueFromJSON(json["total"]),
    isExcluded: json["isExcluded"] == null ? void 0 : json["isExcluded"]
  };
}

// src/rentals-api-v3/models/RewardPointsConfig.ts
function RewardPointsConfigFromJSON(json) {
  return RewardPointsConfigFromJSONTyped(json, false);
}
function RewardPointsConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    eligible: json["eligible"],
    type: json["type"] == null ? void 0 : json["type"],
    baseMultiplier: json["baseMultiplier"] == null ? void 0 : json["baseMultiplier"],
    bonusPoints: json["bonusPoints"] == null ? void 0 : json["bonusPoints"],
    bonusMultiplier: json["bonusMultiplier"] == null ? void 0 : json["bonusMultiplier"],
    maxBasePoints: json["maxBasePoints"],
    maxBonusPoints: json["maxBonusPoints"],
    exchangeRate: json["exchangeRate"]
  };
}

// src/rentals-api-v3/models/TripDetails.ts
function TripDetailsFromJSON(json) {
  return TripDetailsFromJSONTyped(json, false);
}
function TripDetailsFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    pickUpLocation: json["pickUpLocation"],
    dropOffLocation: json["dropOffLocation"],
    pickUpDate: stringToDate(json["pickUpDate"]),
    dropOffDate: stringToDate(json["dropOffDate"]),
    rentalDays: json["rentalDays"],
    totalPrice: CurrencyValueFromJSON(json["totalPrice"]),
    lines: json["lines"] == null ? void 0 : json["lines"].map(ProductLineFromJSON),
    fleetCategory: FleetCategoryFromJSON(json["fleetCategory"]),
    secondaryProducts: json["secondaryProducts"].map(SecondaryProductGroupFromJSON),
    insuranceProducts: InsuranceProductGroupFromJSON(json["insuranceProducts"]),
    bundles: json["bundles"] == null ? void 0 : json["bundles"].map(ProductBundleFromJSON)
  };
}

// src/rentals-api-v3/models/UpdateReservationRequest.ts
function UpdateReservationRequestToJSON(json) {
  return UpdateReservationRequestToJSONTyped(json, false);
}
function UpdateReservationRequestToJSONTyped(value, ignoreDiscriminator = false) {
  if (value == null) {
    return value;
  }
  return {
    pickUpLocation: value["pickUpLocation"],
    dropOffLocation: value["dropOffLocation"],
    pickUpDate: value["pickUpDate"],
    dropOffDate: value["dropOffDate"],
    fleetCategory: value["fleetCategory"],
    driverAge: value["driverAge"],
    numberOfPeople: value["numberOfPeople"],
    campaignCode: value["campaignCode"],
    pascal: value["pascal"],
    insurance: value["insurance"],
    secondaryProducts: value["secondaryProducts"],
    hirerDetails: HirerDetailsSummaryToJSON(value["hirerDetails"]),
    voucherReference: value["voucherReference"],
    gateway: value["gateway"],
    payment: value["payment"]
  };
}

// src/rentals-api-v3/models/UpdateReservationValidationStatus.ts
function UpdateReservationValidationStatusFromJSON(json) {
  return UpdateReservationValidationStatusFromJSONTyped(json, false);
}
function UpdateReservationValidationStatusFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}

// src/rentals-api-v3/models/ValidateUpdateResponse.ts
function ValidateUpdateResponseFromJSON(json) {
  return ValidateUpdateResponseFromJSONTyped(json, false);
}
function ValidateUpdateResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json == null) {
    return json;
  }
  return {
    updates: json["updates"] == null ? void 0 : TripDetailsFromJSON(json["updates"]),
    message: json["message"] == null ? void 0 : json["message"],
    original: json["original"] == null ? void 0 : BookingFromJSON(json["original"]),
    status: UpdateReservationValidationStatusFromJSON(json["status"]),
    reference: json["reference"],
    bookingNo: json["bookingNo"] == null ? void 0 : json["bookingNo"]
  };
}

// src/rentals-api-v3/apis/ReservationsApi.ts
var ReservationsApi = class extends BaseAPI {
  /**
   * Cancel an existing reservation
   */
  async cancelReservationRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling cancelReservation().'
      );
    }
    const queryParameters = {};
    if (requestParameters["onlineLogin"] != null) {
      queryParameters["onlineLogin"] = requestParameters["onlineLogin"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/reservations/{reservationRef}`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "DELETE",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => CancelReservationResponseFromJSON(jsonValue));
  }
  /**
   * Cancel an existing reservation
   */
  async cancelReservation(reservationRef, onlineLogin, initOverrides) {
    const response = await this.cancelReservationRaw({ reservationRef, onlineLogin }, initOverrides);
    return await response.value();
  }
  /**
   * Create a new booking
   */
  async createBookingRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/reservations`;
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: CreateBookingRequestToJSON(requestParameters["createBookingRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => CreateBookingResponseFromJSON(jsonValue));
  }
  /**
   * Create a new booking
   */
  async createBooking(createBookingRequest, initOverrides) {
    const response = await this.createBookingRaw({ createBookingRequest }, initOverrides);
    return await response.value();
  }
  /**
   * Fetch a booking
   */
  async fetchBookingRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling fetchBooking().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/reservations/{reservationRef}`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => BookingFromJSON(jsonValue));
  }
  /**
   * Fetch a booking
   */
  async fetchBooking(reservationRef, initOverrides) {
    const response = await this.fetchBookingRaw({ reservationRef }, initOverrides);
    return await response.value();
  }
  /**
   * Find your reservation
   */
  async findReservationRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/reservations/find`;
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: FindReservationRequestToJSON(requestParameters["findReservationRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => FindReservationResultFromJSON(jsonValue));
  }
  /**
   * Find your reservation
   */
  async findReservation(findReservationRequest, initOverrides) {
    const response = await this.findReservationRaw({ findReservationRequest }, initOverrides);
    return await response.value();
  }
  /**
   * Return options available for a booking
   */
  async getOptionsRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError("reservationRef", 'Required parameter "reservationRef" was null or undefined when calling getOptions().');
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/reservations/{reservationRef}/options`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => BookingOptionsFromJSON(jsonValue));
  }
  /**
   * Return options available for a booking
   */
  async getOptions(reservationRef, initOverrides) {
    const response = await this.getOptionsRaw({ reservationRef }, initOverrides);
    return await response.value();
  }
  /**
   * Retrieve loyalty point status
   */
  async getRewardPointsStatusRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters["fleetType"] != null) {
      queryParameters["fleetType"] = requestParameters["fleetType"];
    }
    if (requestParameters["dealId"] != null) {
      queryParameters["dealId"] = requestParameters["dealId"];
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/rewards/points`;
    const response = await this.request(
      {
        path: urlPath,
        method: "GET",
        headers: headerParameters,
        query: queryParameters
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => jsonValue.map(RewardPointsConfigFromJSON));
  }
  /**
   * Retrieve loyalty point status
   */
  async getRewardPointsStatus(fleetType, dealId, initOverrides) {
    const response = await this.getRewardPointsStatusRaw({ fleetType, dealId }, initOverrides);
    return await response.value();
  }
  /**
   * Make updates and edit an existing reservation
   */
  async updateReservationRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling updateReservation().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/reservations/{reservationRef}`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "PATCH",
        headers: headerParameters,
        query: queryParameters,
        body: UpdateReservationRequestToJSON(requestParameters["updateReservationRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => CreateBookingResponseFromJSON(jsonValue));
  }
  /**
   * Make updates and edit an existing reservation
   */
  async updateReservation(reservationRef, updateReservationRequest, initOverrides) {
    const response = await this.updateReservationRaw(
      { reservationRef, updateReservationRequest },
      initOverrides
    );
    return await response.value();
  }
  /**
   * Validate the update reservation
   */
  async validateUpdateReservationRaw(requestParameters, initOverrides) {
    if (requestParameters["reservationRef"] == null) {
      throw new RequiredError(
        "reservationRef",
        'Required parameter "reservationRef" was null or undefined when calling validateUpdateReservation().'
      );
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.apiKey) {
      headerParameters["X-API-KEY"] = await this.configuration.apiKey("X-API-KEY");
    }
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("bearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    let urlPath = `/api/v3/reservations/{reservationRef}/validate-update-reservation`;
    urlPath = urlPath.replace(`{${"reservationRef"}}`, encodeURIComponent(String(requestParameters["reservationRef"])));
    const response = await this.request(
      {
        path: urlPath,
        method: "POST",
        headers: headerParameters,
        query: queryParameters,
        body: UpdateReservationRequestToJSON(requestParameters["updateReservationRequest"])
      },
      initOverrides
    );
    return new JSONApiResponse(response, (jsonValue) => ValidateUpdateResponseFromJSON(jsonValue));
  }
  /**
   * Validate the update reservation
   */
  async validateUpdateReservation(reservationRef, updateReservationRequest, initOverrides) {
    const response = await this.validateUpdateReservationRaw(
      { reservationRef, updateReservationRequest },
      initOverrides
    );
    return await response.value();
  }
};
export {
  ReservationsApi
};
//# sourceMappingURL=ReservationsApi.mjs.map